import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJournal } from "../context/JournalContext";
import InfoPoint from "../components/InfoPoint";
import {
  Coffee,
  Wine,
  Dumbbell,
  Brain,
  Utensils,
  Smile,
  Moon,
  Sun,
  ChevronRight,
  Trash2,
  Save,
  Notebook,
  ArrowLeft,
} from "lucide-react";

const EXERCISE_OPTS = [
  { id: "none", label: "Nessuno", icon: "😴" },
  { id: "light", label: "Leggero", icon: "🚶" },
  { id: "moderate", label: "Moderato", icon: "🏃" },
  { id: "intense", label: "Intenso", icon: "💪" },
];

const MOOD_OPTS = [
  { id: 1, emoji: "😞", label: "Male" },
  { id: 2, emoji: "😕", label: "Così così" },
  { id: 3, emoji: "😐", label: "Neutro" },
  { id: 4, emoji: "🙂", label: "Bene" },
  { id: 5, emoji: "😄", label: "Benissimo" },
];

function SectionTitle({ icon, children, infoKey }) {
  return (
    <h3 className="text-white text-[16px] font-semibold flex items-center gap-2 mt-5 mb-2">
      <span className="w-7 h-7 rounded-lg bg-[#14c2a1]/15 border border-[#14c2a1]/25 flex items-center justify-center text-[#14c2a1]">
        {icon}
      </span>
      {children}
      {infoKey && <InfoPoint keyName={infoKey} />}
    </h3>
  );
}

function Counter({ value, setValue, min = 0, max = 10 }) {
  return (
    <div className="flex items-center gap-3">
      <button
        onClick={() => setValue(Math.max(min, value - 1))}
        className="w-9 h-9 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors flex items-center justify-center"
      >
        −
      </button>
      <div className="text-white text-2xl font-semibold w-10 text-center">
        {value}
      </div>
      <button
        onClick={() => setValue(Math.min(max, value + 1))}
        className="w-9 h-9 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors flex items-center justify-center"
      >
        +
      </button>
    </div>
  );
}

function formatItDate(isoDate) {
  try {
    const d = new Date(isoDate + "T12:00:00");
    return d.toLocaleDateString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
  } catch {
    return isoDate;
  }
}

export default function Diario() {
  const navigate = useNavigate();
  const { getEntry, saveEntry, recentEntries, todayKey, removeEntry } =
    useJournal();
  const [date] = useState(todayKey());
  const existing = getEntry(date);

  const [mood, setMood] = useState(existing?.mood || 3);
  const [breakfast, setBreakfast] = useState(existing?.breakfast || "");
  const [lunch, setLunch] = useState(existing?.lunch || "");
  const [dinner, setDinner] = useState(existing?.dinner || "");
  const [snacks, setSnacks] = useState(existing?.snacks || "");
  const [caffeine, setCaffeine] = useState(existing?.caffeine ?? 1);
  const [alcohol, setAlcohol] = useState(existing?.alcohol ?? 0);
  const [water, setWater] = useState(existing?.water ?? 5);
  const [exercise, setExercise] = useState(existing?.exercise || "none");
  const [stress, setStress] = useState(existing?.stress ?? 4);
  const [activities, setActivities] = useState(existing?.activities || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [savedToast, setSavedToast] = useState(false);

  const recent = useMemo(() => recentEntries(7), [recentEntries]);

  const handleSave = () => {
    saveEntry(
      {
        mood,
        breakfast,
        lunch,
        dinner,
        snacks,
        caffeine,
        alcohol,
        water,
        exercise,
        stress,
        activities,
        notes,
      },
      date
    );
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 1600);
  };

  return (
    <div className="px-4 pt-4 pb-36 relative">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          aria-label="indietro"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="text-white/70 text-[12px] uppercase tracking-[0.2em]">
          Diario giornaliero
        </div>
        <div className="w-10" />
      </div>

      <h1 className="text-white text-[28px] font-semibold tracking-tight capitalize">
        {formatItDate(date)}
      </h1>
      <p className="text-white/60 text-[13px] mt-1">
        Raccontaci come è andata la tua giornata. Ci aiuta a darti consigli
        migliori.
      </p>

      {/* Mood */}
      <SectionTitle icon={<Smile size={14} />} infoKey="diary_mood">Come ti senti oggi?</SectionTitle>
      <div className="flex items-center justify-between gap-1">
        {MOOD_OPTS.map((m) => (
          <button
            key={m.id}
            onClick={() => setMood(m.id)}
            className={`flex-1 h-16 rounded-2xl text-2xl transition-all border ${
              mood === m.id
                ? "bg-[#14c2a1]/15 border-[#14c2a1] scale-[1.03]"
                : "bg-white/5 border-white/10 hover:bg-white/10"
            }`}
            aria-label={m.label}
          >
            {m.emoji}
          </button>
        ))}
      </div>

      {/* Meals */}
      <SectionTitle icon={<Utensils size={14} />} infoKey="diary_meals">Cosa hai mangiato</SectionTitle>
      <div className="space-y-3">
        {[
          { key: "breakfast", label: "Colazione", val: breakfast, set: setBreakfast },
          { key: "lunch", label: "Pranzo", val: lunch, set: setLunch },
          { key: "dinner", label: "Cena", val: dinner, set: setDinner },
          { key: "snacks", label: "Spuntini", val: snacks, set: setSnacks },
        ].map((m) => (
          <div
            key={m.key}
            className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 focus-within:border-[#14c2a1] focus-within:ring-2 focus-within:ring-[#14c2a1]/25 transition-all"
          >
            <label className="text-white/60 text-[11px] uppercase tracking-wider">
              {m.label}
            </label>
            <input
              value={m.val}
              onChange={(e) => m.set(e.target.value)}
              placeholder={
                m.key === "breakfast"
                  ? "Es. caffè, yogurt, frutta"
                  : m.key === "lunch"
                  ? "Es. pasta, insalata"
                  : m.key === "dinner"
                  ? "Es. pesce, verdure"
                  : "Es. biscotti, frutta secca"
              }
              className="w-full mt-0.5 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
            />
          </div>
        ))}
      </div>

      {/* Drinks */}
      <SectionTitle icon={<Coffee size={14} />} infoKey="diary_caffeine">Bevande</SectionTitle>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
          <div className="flex items-center gap-2 text-white text-[13px] font-medium">
            <Coffee size={14} className="text-[#67e8d1]" /> Caffè / Tè
          </div>
          <div className="mt-2">
            <Counter value={caffeine} setValue={setCaffeine} max={10} />
          </div>
          <div className="text-white/50 text-[11px] mt-1">tazze</div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
          <div className="flex items-center gap-2 text-white text-[13px] font-medium">
            <Wine size={14} className="text-[#67e8d1]" /> Alcol
          </div>
          <div className="mt-2">
            <Counter value={alcohol} setValue={setAlcohol} max={10} />
          </div>
          <div className="text-white/50 text-[11px] mt-1">bicchieri</div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3 col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white text-[13px] font-medium">
              💧 Acqua
            </div>
            <div className="text-white/70 text-[12px]">{water} bicchieri</div>
          </div>
          <input
            type="range"
            min="0"
            max="15"
            value={water}
            onChange={(e) => setWater(parseInt(e.target.value, 10))}
            className="w-full mt-2 accent-[#14c2a1]"
          />
        </div>
      </div>

      {/* Exercise */}
      <SectionTitle icon={<Dumbbell size={14} />} infoKey="diary_exercise">Attività fisica</SectionTitle>
      <div className="grid grid-cols-4 gap-2">
        {EXERCISE_OPTS.map((o) => (
          <button
            key={o.id}
            onClick={() => setExercise(o.id)}
            className={`rounded-2xl py-3 text-center transition-all border ${
              exercise === o.id
                ? "bg-[#14c2a1]/15 border-[#14c2a1]"
                : "bg-white/5 border-white/10 hover:bg-white/10"
            }`}
          >
            <div className="text-xl">{o.icon}</div>
            <div className="text-white/85 text-[11px] mt-1">{o.label}</div>
          </button>
        ))}
      </div>

      {/* Stress */}
      <SectionTitle icon={<Brain size={14} />} infoKey="diary_stress">
        Livello di stress ({stress}/10)
      </SectionTitle>
      <input
        type="range"
        min="1"
        max="10"
        value={stress}
        onChange={(e) => setStress(parseInt(e.target.value, 10))}
        className="w-full accent-[#14c2a1]"
      />
      <div className="flex items-center justify-between text-white/50 text-[11px] mt-1">
        <span>Rilassato</span>
        <span>Molto stressato</span>
      </div>

      {/* Activities */}
      <SectionTitle icon={<Notebook size={14} />}>
        Cosa hai fatto oggi?
      </SectionTitle>
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3 focus-within:border-[#14c2a1] focus-within:ring-2 focus-within:ring-[#14c2a1]/25 transition-all">
        <textarea
          value={activities}
          onChange={(e) => setActivities(e.target.value)}
          placeholder="Es. lavoro al computer, ho visto amici, ho guardato un film…"
          rows={3}
          className="w-full bg-transparent text-white text-sm placeholder-white/40 focus:outline-none resize-none"
        />
      </div>

      {/* Notes */}
      <SectionTitle icon={<Moon size={14} />}>Note per il sonno</SectionTitle>
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3 focus-within:border-[#14c2a1] focus-within:ring-2 focus-within:ring-[#14c2a1]/25 transition-all">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Pensieri, preoccupazioni, intenzioni per domani…"
          rows={3}
          className="w-full bg-transparent text-white text-sm placeholder-white/40 focus:outline-none resize-none"
        />
      </div>

      <button
        onClick={handleSave}
        className="mt-6 w-full h-12 rounded-2xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 transition-all"
        style={{
          background: "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
          boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
        }}
      >
        <Save size={16} /> Salva diario
      </button>

      {/* Recent entries */}
      {recent.length > 0 && (
        <section className="mt-8">
          <h3 className="text-white text-[18px] font-semibold">
            Ultimi giorni
          </h3>
          <div className="mt-3 space-y-2">
            {recent.map((r) => (
              <div
                key={r.date}
                className="flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">
                    {MOOD_OPTS.find((m) => m.id === r.mood)?.emoji || "📝"}
                  </span>
                  <div>
                    <div className="text-white text-[14px] font-medium capitalize">
                      {formatItDate(r.date)}
                    </div>
                    <div className="text-white/60 text-[11px]">
                      Caff. {r.caffeine || 0} · Alc. {r.alcohol || 0} · Stress{" "}
                      {r.stress || 0}/10
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeEntry(r.date)}
                  className="w-8 h-8 rounded-full hover:bg-rose-400/10 text-rose-300 flex items-center justify-center transition-colors"
                  aria-label="elimina"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {savedToast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 px-4 py-2 rounded-full bg-[#14c2a1] text-[#0b3a34] text-sm font-medium shadow-xl">
          Diario salvato ✓
        </div>
      )}
    </div>
  );
}
